// /client/src/SensorDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Thermometer,
  Droplets,
  Activity,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  AlertCircle,
  Info,
  X
} from 'lucide-react';

import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const SensorDashboard = ({ user }) => {
  const [sensorData, setSensorData] = useState({
    latest: null,
    stats: null,
    detailedData: [],
    hourlyData: [],
    alerts: [],
    config: null,
    loading: true,
    error: null
  });

  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [chartView, setChartView] = useState('combined'); // combined, temperature, humidity
  const [chartPeriod, setChartPeriod] = useState('detailed'); // detailed (15min), hourly

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusReadings, setStatusReadings] = useState([]);

  // Fetch all sensor data
  const fetchSensorData = useCallback(async () => {
    try {
      setSensorData(prev => ({ ...prev, loading: true, error: null }));

      const [
        latestResponse,
        detailedResponse,
        statsResponse,
        hourlyResponse,
        alertsResponse,
        configResponse
      ] = await Promise.all([
        fetch('/api/sensor-data/latest'),
        fetch(`/api/sensor-data?range=${timeRange}`),
        fetch(`/api/sensor-data/stats?range=${timeRange}`),
        fetch(`/api/sensor-data/hourly?range=${timeRange}`),
        fetch('/api/sensor-data/alerts?resolved=false'),
        fetch('/api/sensor-data/config')
      ]);

      const latestJson = await latestResponse.json();
      const detailedJson = await detailedResponse.json();
      const statsJson = await statsResponse.json();
      const hourlyJson = await hourlyResponse.json();
      const alertsJson = await alertsResponse.json();
      const configJson = await configResponse.json();

      setSensorData({
        latest: latestJson?.data ?? null,
        stats: statsJson?.overall ?? null,
        detailedData: detailedJson?.data ?? [],
        hourlyData: hourlyJson?.data ?? [],
        alerts: alertsJson?.data ?? [],
        config: configJson?.data ?? null,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to fetch sensor data:', error);
      setSensorData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load sensor data'
      }));
    }
  }, [timeRange]);

  useEffect(() => {
    fetchSensorData();

    if (!autoRefresh) return;

    const interval = setInterval(fetchSensorData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSensorData, autoRefresh]);

  // Helper functions
  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'optimal':
        return { bg: '#dcfce7', text: '#15803d', border: '#86efac' };
      case 'warning':
        return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
      case 'critical':
        return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
    }
  };

  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'optimal':
        return <CheckCircle size={20} />;
      case 'warning':
        return <AlertTriangle size={20} />;
      case 'critical':
        return <AlertCircle size={20} />;
      default:
        return <Activity size={20} />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  // Chart helpers (formatChartTime must be defined before getChartData)
  const formatChartTime = (timestamp, period) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);

    if (period === 'detailed') {
      const hours = date.getHours();
      const mins = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${String(mins).padStart(2, '0')} ${ampm}`;
    }

    const hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours} ${ampm}`;
  };

  const round1 = (n) => {
    const x = Number.parseFloat(n);
    if (Number.isNaN(x)) return null;
    return Math.round(x * 10) / 10;
  };

  const chartData = useMemo(() => {
    if (chartPeriod === 'detailed') {
      return (sensorData.detailedData || []).map(item => ({
        time: formatChartTime(item.timestamp, 'detailed'),
        fullTime: item.timestamp,
        temperature: round1(item.temperature_f),
        humidity: round1(item.humidity),
        status: item.status
      }));
    }

    return (sensorData.hourlyData || []).map(item => ({
      time: formatChartTime(item.hour, 'hourly'),
      fullTime: item.hour,
      temperature: round1(item.avg_temp_f),
      tempMin: round1(item.min_temp_f),
      tempMax: round1(item.max_temp_f),
      humidity: round1(item.avg_humidity),
      humidityMin: round1(item.min_humidity),
      humidityMax: round1(item.max_humidity)
    }));
  }, [chartPeriod, sensorData.detailedData, sensorData.hourlyData]);

  // Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '0.75rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#1f2937' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color, fontSize: '0.875rem', margin: '0.25rem 0' }}>
            {entry.name}: {entry.value}{String(entry.name).toLowerCase().includes('temp') ? '°F' : '%'}
          </p>
        ))}
        {chartPeriod === 'detailed' && payload[0]?.payload?.status && (
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', textTransform: 'uppercase' }}>
            Status: {payload[0].payload.status}
          </p>
        )}
      </div>
    );
  };

  const handleStatusClick = async (status) => {
    setSelectedStatus(status);
    setShowStatusModal(true);
    setStatusReadings([]);

    try {
      const response = await fetch(`/api/sensor-data?range=${timeRange}`);
      const data = await response.json();

      if (data?.success) {
        const filtered = (data.data || []).filter(r =>
          String(r.status || '').toLowerCase() === String(status || '').toLowerCase()
        );
        setStatusReadings(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch status readings:', error);
    }
  };

  const getCurrentStatusReason = () => {
    if (!sensorData.latest || !sensorData.config) return [];

    const { temperature_f, humidity } = sensorData.latest;
    const {
      temp_min_optimal, temp_max_optimal, temp_min_acceptable, temp_max_acceptable,
      humidity_min_optimal, humidity_max_optimal, humidity_min_acceptable, humidity_max_acceptable
    } = sensorData.config;

    const reasons = [];

    const temp = parseFloat(temperature_f);
    if (temp < parseFloat(temp_min_acceptable) || temp > parseFloat(temp_max_acceptable)) {
      reasons.push(`Temperature ${temp.toFixed(1)}°F is outside acceptable range (${temp_min_acceptable}-${temp_max_acceptable}°F)`);
    } else if (temp < parseFloat(temp_min_optimal) || temp > parseFloat(temp_max_optimal)) {
      reasons.push(`Temperature ${temp.toFixed(1)}°F is outside optimal range (${temp_min_optimal}-${temp_max_optimal}°F)`);
    }

    const humid = parseFloat(humidity);
    if (humid < parseFloat(humidity_min_acceptable) || humid > parseFloat(humidity_max_acceptable)) {
      reasons.push(`Humidity ${humid.toFixed(1)}% is outside acceptable range (${humidity_min_acceptable}-${humidity_max_acceptable}%)`);
    } else if (humid < parseFloat(humidity_min_optimal) || humid > parseFloat(humidity_max_optimal)) {
      reasons.push(`Humidity ${humid.toFixed(1)}% is outside optimal range (${humidity_min_optimal}-${humidity_max_optimal}%)`);
    }

    return reasons.length > 0 ? reasons : ['All conditions within optimal range'];
  };

  const statusReasons = getCurrentStatusReason();

  const StatusModal = () => {
    if (!showStatusModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }} onClick={() => setShowStatusModal(false)}>
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }} onClick={(e) => e.stopPropagation()}>

          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', margin: 0, textTransform: 'capitalize' }}>
              {selectedStatus} Readings ({statusReadings.length})
            </h2>
            <button
              onClick={() => setShowStatusModal(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            >
              <X size={24} color="#6b7280" />
            </button>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Timestamp</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Temperature</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Humidity</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {statusReadings.map((reading, index) => {
                    const sc = getStatusColor(reading.status);
                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem', color: '#374151' }}>
                          {new Date(reading.timestamp).toLocaleString()}
                        </td>
                        <td style={{ padding: '0.75rem', color: '#374151', fontWeight: '500' }}>
                          {Number.parseFloat(reading.temperature_f).toFixed(1)}°F
                        </td>
                        <td style={{ padding: '0.75rem', color: '#374151', fontWeight: '500' }}>
                          {Number.parseFloat(reading.humidity).toFixed(1)}%
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '1rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: sc.bg,
                            color: sc.text
                          }}>
                            {reading.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {statusReadings.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: '1rem', color: '#6b7280', textAlign: 'center' }}>
                        No readings found for this status.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  };

  // Loading state
  if (sensorData.loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '3rem'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }} />
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          Loading sensor data...
        </p>
      </div>
    );
  }

  const { latest, stats, config, alerts, error } = sensorData;
  const statusColors = getStatusColor(latest?.status);

  if (error) {
    return (
      <div style={{ padding: '2rem', color: '#991b1b' }}>
        <p style={{ fontWeight: '700' }}>Error</p>
        <p>{error}</p>
        <button
          onClick={fetchSensorData}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1600px', margin: '0 auto' }}>
      <StatusModal />

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
              Environmental Monitoring
            </h1>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>
              {config?.location || 'Regional Cardiology Department'} - {config?.equipment_monitored || 'Equipment'}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={fetchSensorData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1rem',
              background: autoRefresh ? '#dcfce7' : '#f3f4f6',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ color: '#374151', fontWeight: '500' }}>Auto refresh (2min)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Status Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>

        {/* Current Temperature */}
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          color: 'white',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <Thermometer size={32} style={{ opacity: 0.9 }} />
            <div style={{
              padding: '0.375rem 0.75rem',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              LIVE
            </div>
          </div>
          <div style={{ fontSize: '3rem', fontWeight: '800', lineHeight: '1', marginBottom: '0.5rem' }}>
            {latest?.temperature_f ? `${Number.parseFloat(latest.temperature_f).toFixed(1)}°F` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Current Temperature</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
            {latest?.temperature_c ? `${Number.parseFloat(latest.temperature_c).toFixed(1)}°C` : ''}
          </div>
        </div>

        {/* Current Humidity */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          color: 'white',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <Droplets size={32} style={{ opacity: 0.9 }} />
            <div style={{
              padding: '0.375rem 0.75rem',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              LIVE
            </div>
          </div>
          <div style={{ fontSize: '3rem', fontWeight: '800', lineHeight: '1', marginBottom: '0.5rem' }}>
            {latest?.humidity ? `${Number.parseFloat(latest.humidity).toFixed(1)}%` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Relative Humidity</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>RH Level</div>
        </div>

        {/* System Status */}
        <div style={{
          background: 'white',
          border: `2px solid ${statusColors.border}`,
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <Activity size={28} color={statusColors.text} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div style={{ color: statusColors.text }}>{getStatusIcon(latest?.status)}</div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: statusColors.text,
              textTransform: 'uppercase'
            }}>
              {latest?.status || 'UNKNOWN'}
            </div>
          </div>

          <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>System Status</div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
            {(alerts || []).length} active alert{(alerts || []).length !== 1 ? 's' : ''}
          </div>

          {statusReasons && statusReasons.length > 0 && (
            <div style={{
              fontSize: '0.75rem',
              color: statusColors.text,
              background: statusColors.bg,
              padding: '0.5rem',
              borderRadius: '0.375rem',
              marginTop: '0.75rem'
            }}>
              {statusReasons.map((reason, i) => (
                <div key={i} style={{ marginBottom: i < statusReasons.length - 1 ? '0.25rem' : 0 }}>
                  {`• ${reason}`}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Last Updated */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <Clock size={28} color="#6b7280" />
          </div>
          <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
            {formatTimestamp(latest?.timestamp)}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Last Reading</div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
            Next: {Math.ceil((15 - (new Date().getMinutes() % 15)))} min
          </div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {['1h', '24h', '7d', '30d'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              style={{
                padding: '0.5rem 1rem',
                background: timeRange === range ? '#2563eb' : 'white',
                color: timeRange === range ? 'white' : '#374151',
                border: timeRange === range ? 'none' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {range === '1h' ? '1 Hour' : range === '24h' ? '24 Hours' : range === '7d' ? '7 Days' : '30 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Statistics Grid (kept as you had it; uses stats safely) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            AVG TEMP ({timeRange})
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.25rem' }}>
            {stats?.avg_temp_f ? `${Number.parseFloat(stats.avg_temp_f).toFixed(1)}°F` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            {stats?.avg_temp_f ? `${((Number.parseFloat(stats.avg_temp_f) - 32) * 5 / 9).toFixed(1)}°C` : ''}
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            MIN TEMP
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#3b82f6', marginBottom: '0.25rem' }}>
            {stats?.min_temp_f ? `${Number.parseFloat(stats.min_temp_f).toFixed(1)}°F` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Lowest recorded</div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            MAX TEMP
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#ef4444', marginBottom: '0.25rem' }}>
            {stats?.max_temp_f ? `${Number.parseFloat(stats.max_temp_f).toFixed(1)}°F` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Highest recorded</div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            AVG HUMIDITY
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.25rem' }}>
            {stats?.avg_humidity ? `${Number.parseFloat(stats.avg_humidity).toFixed(1)}%` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Relative humidity</div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            STD DEV
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.25rem' }}>
            {stats?.stddev_temp_f ? `±${Number.parseFloat(stats.stddev_temp_f).toFixed(2)}°` : 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Temperature variance</div>
        </div>

        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.25rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            DATA POINTS
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.25rem' }}>
            {stats?.total_readings ?? 0}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Readings collected</div>
        </div>
      </div>

      {/* Chart selectors (fixed structure, no JSX inside map that breaks compilation) */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Period:</span>
          {[
            { value: 'detailed', label: '15 Minute Detail' },
            { value: 'hourly', label: 'Hourly Average' }
          ].map(period => (
            <button
              key={period.value}
              onClick={() => setChartPeriod(period.value)}
              style={{
                padding: '0.5rem 1rem',
                background: chartPeriod === period.value ? '#8b5cf6' : 'white',
                color: chartPeriod === period.value ? 'white' : '#374151',
                border: chartPeriod === period.value ? 'none' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {period.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { value: 'combined', label: 'Combined View' },
            { value: 'temperature', label: 'Temperature Only' },
            { value: 'humidity', label: 'Humidity Only' }
          ].map(view => (
            <button
              key={view.value}
              onClick={() => setChartView(view.value)}
              style={{
                padding: '0.5rem 1rem',
                background: chartView === view.value ? '#10b981' : 'white',
                color: chartView === view.value ? 'white' : '#374151',
                border: chartView === view.value ? 'none' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Charts Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Main Chart Area */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            {chartView === 'combined' && 'Temperature and Humidity Trend'}
            {chartView === 'temperature' && 'Temperature Trend'}
            {chartView === 'humidity' && 'Humidity Trend'}{' '}
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '400' }}>
              ({chartPeriod === 'detailed' ? '15 Minute' : 'Hourly'} Data)
            </span>
          </h2>

          {chartData.length === 0 ? (
            <div style={{
              background: '#f9fafb',
              borderRadius: '0.5rem',
              padding: '3rem',
              textAlign: 'center',
              border: '2px dashed #e5e7eb'
            }}>
              <Activity size={48} color="#9ca3af" style={{ marginBottom: '1rem' }} />
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                No data available for selected time range
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              {chartView === 'combined' ? (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" style={{ fontSize: '0.75rem' }} />
                  <YAxis
                    yAxisId="temp"
                    stroke="#ef4444"
                    style={{ fontSize: '0.75rem' }}
                    label={{ value: 'Temperature (°F)', angle: -90, position: 'insideLeft', style: { fontSize: '0.75rem' } }}
                  />
                  <YAxis
                    yAxisId="humidity"
                    orientation="right"
                    stroke="#3b82f6"
                    style={{ fontSize: '0.75rem' }}
                    label={{ value: 'Humidity (%)', angle: 90, position: 'insideRight', style: { fontSize: '0.75rem' } }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  {config && (
                    <>
                      <ReferenceLine yAxisId="temp" y={parseFloat(config.temp_min_optimal)} stroke="#10b981" strokeDasharray="3 3" />
                      <ReferenceLine yAxisId="temp" y={parseFloat(config.temp_max_optimal)} stroke="#10b981" strokeDasharray="3 3" />
                    </>
                  )}

                  <Line
                    yAxisId="temp"
                    type="monotone"
                    dataKey="temperature"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ fill: '#ef4444', r: chartPeriod === 'detailed' ? 2 : 3 }}
                    name="Temperature"
                  />
                  <Line
                    yAxisId="humidity"
                    type="monotone"
                    dataKey="humidity"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: chartPeriod === 'detailed' ? 2 : 3 }}
                    name="Humidity"
                  />
                </LineChart>
              ) : chartView === 'temperature' ? (
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorTemp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" style={{ fontSize: '0.75rem' }} />
                  <YAxis
                    stroke="#6b7280"
                    style={{ fontSize: '0.75rem' }}
                    label={{ value: 'Temperature (°F)', angle: -90, position: 'insideLeft', style: { fontSize: '0.75rem' } }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  {config && (
                    <>
                      <ReferenceLine y={parseFloat(config.temp_min_optimal)} stroke="#10b981" strokeDasharray="3 3" />
                      <ReferenceLine y={parseFloat(config.temp_max_optimal)} stroke="#10b981" strokeDasharray="3 3" />
                    </>
                  )}

                  <Area
                    type="monotone"
                    dataKey="temperature"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorTemp)"
                    name="Temperature"
                  />
                </AreaChart>
              ) : (
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorHumidity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" style={{ fontSize: '0.75rem' }} />
                  <YAxis
                    stroke="#6b7280"
                    style={{ fontSize: '0.75rem' }}
                    label={{ value: 'Humidity (%)', angle: -90, position: 'insideLeft', style: { fontSize: '0.75rem' } }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  {config && (
                    <>
                      <ReferenceLine y={parseFloat(config.humidity_min_optimal)} stroke="#10b981" strokeDasharray="3 3" />
                      <ReferenceLine y={parseFloat(config.humidity_max_optimal)} stroke="#10b981" strokeDasharray="3 3" />
                    </>
                  )}

                  <Area
                    type="monotone"
                    dataKey="humidity"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorHumidity)"
                    name="Humidity"
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          )}
        </div>

        {/* Status Summary (fixed: correct status clicks and counts) */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
            Status Summary
          </h2>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>
            Click to view detailed readings
          </p>

          {/* Optimal */}
          <div
            onClick={() => handleStatusClick('optimal')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem',
              background: '#dcfce7',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: '2px solid transparent',
              marginBottom: '0.75rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.borderColor = '#86efac';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle size={18} color="#15803d" />
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#15803d' }}>Optimal</span>
            </div>
            <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#15803d' }}>
              {stats?.optimal_count ?? 0}
            </span>
          </div>

          {/* Warning */}
          <div
            onClick={() => handleStatusClick('warning')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem',
              background: '#fef3c7',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: '2px solid transparent',
              marginBottom: '0.75rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.borderColor = '#fcd34d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={18} color="#92400e" />
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#92400e' }}>Warning</span>
            </div>
            <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e' }}>
              {stats?.warning_count ?? 0}
            </span>
          </div>

          {/* Critical */}
          <div
            onClick={() => handleStatusClick('critical')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem',
              background: '#fee2e2',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              border: '2px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.borderColor = '#fca5a5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertCircle size={18} color="#991b1b" />
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#991b1b' }}>Critical</span>
            </div>
            <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#991b1b' }}>
              {stats?.critical_count ?? 0}
            </span>
          </div>

          {/* Threshold Info */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Info size={16} color="#6b7280" />
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                Thresholds
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#4b5563', lineHeight: '1.5' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Temp Optimal:</strong> {config?.temp_min_optimal}°F - {config?.temp_max_optimal}°F
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Temp Acceptable:</strong> {config?.temp_min_acceptable}°F - {config?.temp_max_acceptable}°F
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Humidity Optimal:</strong> {config?.humidity_min_optimal}% - {config?.humidity_max_optimal}%
              </div>
              <div>
                <strong>Humidity Acceptable:</strong> {config?.humidity_min_acceptable}% - {config?.humidity_max_acceptable}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Alerts */}
      {(alerts || []).length > 0 && (
        <div style={{
          background: 'white',
          border: '2px solid #fca5a5',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <AlertTriangle size={24} color="#dc2626" />
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#dc2626', margin: 0 }}>
              Active Alerts ({alerts.length})
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {alerts.slice(0, 5).map((alert, index) => (
              <div key={index} style={{
                padding: '1rem',
                background: alert.severity === 'critical' ? '#fee2e2' : '#fef3c7',
                borderRadius: '0.5rem',
                border: `1px solid ${alert.severity === 'critical' ? '#fca5a5' : '#fcd34d'}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: alert.severity === 'critical' ? '#991b1b' : '#92400e',
                    textTransform: 'uppercase'
                  }}>
                    {alert.severity} - {alert.alert_type}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {formatTimestamp(alert.created_at)}
                  </span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0 }}>
                  {alert.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        textAlign: 'center'
      }}>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
          Monitoring: <strong>{config?.equipment_monitored || 'Equipment'}</strong> | Location: <strong>{config?.location || 'Unknown'}</strong> | Readings every 15 minutes | {chartData.length} data points displayed | Auto refresh: {autoRefresh ? 'Enabled' : 'Disabled'}
        </p>
      </div>
    </div>
  );
};

export default SensorDashboard;
